import { createTheme } from "@mui/material/styles";

// 科技感配色方案
const colors = {
  // 主色调 - 科技蓝/青色
  primary: {
    main: "#00d4ff",
    light: "#5ce1ff",
    dark: "#00a8cc",
    gradient: "linear-gradient(135deg, #00d4ff 0%, #0099ff 50%, #7c3aed 100%)",
  },
  // 强调色 - 紫色
  accent: {
    main: "#7c3aed",
    light: "#a78bfa",
    dark: "#5b21b6",
    gradient: "linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)",
  },
  // 成功/次要色 - 青绿色
  secondary: {
    main: "#10b981",
    light: "#34d399",
    dark: "#059669",
    gradient: "linear-gradient(135deg, #10b981 0%, #06b6d4 100%)",
  },
  // 深色背景
  dark: {
    bg: "#0a0a0f",
    card: "#12121a",
    elevated: "#1a1a24",
    border: "#2a2a3a",
  },
  // 浅色背景
  light: {
    bg: "#f8fafc",
    card: "#ffffff",
    elevated: "#f1f5f9",
    border: "#e2e8f0",
  },
  // 文字颜色
  text: {
    primary: "#f8fafc",
    secondary: "#94a3b8",
    muted: "#64748b",
    inverse: "#0f172a",
  },
};

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
      contrastText: "#0a0a0f",
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
      dark: colors.secondary.dark,
      contrastText: "#ffffff",
    },
    background: {
      default: colors.light.bg,
      paper: colors.light.card,
    },
    text: {
      primary: "#0f172a",
      secondary: "#475569",
    },
    grey: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
    },
  },
  typography: {
    fontFamily:
      '"Inter", "Noto Sans SC", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "3.5rem",
      fontWeight: 800,
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
      "@media (max-width:600px)": {
        fontSize: "2.25rem",
      },
    },
    h2: {
      fontSize: "2.5rem",
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: "-0.01em",
      "@media (max-width:600px)": {
        fontSize: "1.875rem",
      },
    },
    h3: {
      fontSize: "1.875rem",
      fontWeight: 600,
      lineHeight: 1.3,
      "@media (max-width:600px)": {
        fontSize: "1.5rem",
      },
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
      lineHeight: 1.35,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.7,
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.7,
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
      letterSpacing: "0.01em",
    },
  },
  shape: {
    borderRadius: 16,
  },
  shadows: [
    "none",
    "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: "12px 28px",
          fontSize: "0.95rem",
          fontWeight: 600,
        },
        containedPrimary: {
          background: colors.primary.gradient,
          color: "#0a0a0f",
          boxShadow:
            "0 0 20px rgba(0, 212, 255, 0.3), 0 4px 15px rgba(0, 212, 255, 0.2)",
          "&:hover": {
            background: colors.primary.gradient,
            boxShadow:
              "0 0 30px rgba(0, 212, 255, 0.4), 0 6px 20px rgba(0, 212, 255, 0.3)",
            transform: "translateY(-2px)",
          },
        },
        containedSecondary: {
          background: colors.secondary.gradient,
          boxShadow:
            "0 0 20px rgba(16, 185, 129, 0.3), 0 4px 15px rgba(16, 185, 129, 0.2)",
          "&:hover": {
            background: colors.secondary.gradient,
            boxShadow:
              "0 0 30px rgba(16, 185, 129, 0.4), 0 6px 20px rgba(16, 185, 129, 0.3)",
            transform: "translateY(-2px)",
          },
        },
        outlined: {
          borderWidth: 2,
          "&:hover": {
            borderWidth: 2,
            background: "rgba(0, 212, 255, 0.05)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          border: "1px solid rgba(0, 0, 0, 0.05)",
          backdropFilter: "blur(10px)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 500,
        },
      },
    },
  },
});

// 导出颜色常量供组件使用
export { colors };
export default theme;
